import React, { useState, useEffect } from 'react';
import { RefreshCw, Users } from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { supabase } from '../../lib/supabase';
import CreateSubscriptionModal from '../CreateSubscriptionModal';
import EditSubscriptionModal from '../EditSubscriptionModal';

export default function SubscriptionsPanel() {
  const { selectedBusiness } = useBusiness();
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
  const [subscriptionFilter, setSubscriptionFilter] = useState<'all' | 'active' | 'past_due' | 'pending' | 'cancelled' | 'expired'>('active');
  const [isCreateSubscriptionModalOpen, setIsCreateSubscriptionModalOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<any | null>(null);

  const loadSubscriptions = async () => {
    if (!selectedBusiness?.id) return;
    setLoadingSubscriptions(true);
    try {
      const { data, error } = await supabase
        .from('subscription_services')
        .select('*, customers:customer_id(id, first_name, last_name, email, phone)')
        .eq('business_id', selectedBusiness.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setSubscriptions(data || []);
    } catch (err) {
      console.error('Error loading subscriptions:', err);
    } finally {
      setLoadingSubscriptions(false);
    }
  };

  useEffect(() => {
    if (selectedBusiness?.id) loadSubscriptions();
  }, [selectedBusiness?.id]);

  const filteredSubscriptions = subscriptions.filter(sub => {
    if (subscriptionFilter === 'all') return true;
    return sub.status === subscriptionFilter;
  });

  const subscriptionCounts = {
    all: subscriptions.length,
    active: subscriptions.filter(s => s.status === 'active').length,
    past_due: subscriptions.filter(s => s.status === 'past_due').length,
    pending: subscriptions.filter(s => s.status === 'pending').length,
    cancelled: subscriptions.filter(s => s.status === 'cancelled').length,
    expired: subscriptions.filter(s => s.status === 'expired').length,
  };

  const getNextRenewal = (sub: any): { date: Date; daysUntil: number } | null => {
    const now = new Date();

    if (sub.next_bill_date && (sub.status === 'active' || sub.status === 'past_due')) {
      const next = new Date(sub.next_bill_date + 'T00:00:00');
      const daysUntil = Math.ceil((next.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      return { date: next, daysUntil };
    }

    if (sub.status !== 'active' || !sub.activated_at) return null;
    const activated = new Date(sub.activated_at);
    let next = new Date(activated);

    const maxIterations = 5000;
    let i = 0;
    while (next <= now && i < maxIterations) {
      switch (sub.interval) {
        case 'day': next.setDate(next.getDate() + (sub.interval_count || 1)); break;
        case 'week': next.setDate(next.getDate() + 7 * (sub.interval_count || 1)); break;
        case 'month': next.setMonth(next.getMonth() + (sub.interval_count || 1)); break;
        case 'year': next.setFullYear(next.getFullYear() + (sub.interval_count || 1)); break;
      }
      i++;
    }

    if (sub.custom_end_date && next > new Date(sub.custom_end_date)) return null;

    const daysUntil = Math.ceil((next.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return { date: next, daysUntil };
  };

  const subscriptionModeBadge = (sub: any): { label: string; cls: string } | null => {
    if (sub.collection_mode === 'card') return { label: 'Auto-charge', cls: 'bg-blue-50 text-blue-700 ring-1 ring-blue-600/20' };
    if (sub.collection_mode === 'invoice') return { label: 'Invoice-only', cls: 'bg-blue-50 text-blue-700 ring-1 ring-blue-600/20' };
    if (sub.billing_mode === 'manual') return { label: 'Manual (legacy)', cls: 'bg-gray-100 text-gray-600 ring-1 ring-gray-500/20' };
    return { label: 'Stripe (legacy)', cls: 'bg-gray-100 text-gray-600 ring-1 ring-gray-500/20' };
  };

  const subscriptionStatusBadge = (status: string): { label: string; cls: string; dot: string } => {
    switch (status) {
      case 'active':
        return { label: 'Active', cls: 'bg-green-50 text-green-700 ring-1 ring-green-600/20', dot: 'bg-green-500' };
      case 'past_due':
        return { label: 'Past due', cls: 'bg-orange-50 text-orange-700 ring-1 ring-orange-600/20', dot: 'bg-orange-500' };
      case 'pending':
        return { label: 'Pending', cls: 'bg-yellow-50 text-yellow-700 ring-1 ring-yellow-600/20', dot: 'bg-yellow-500' };
      case 'cancelled':
        return { label: 'Cancelled', cls: 'bg-red-50 text-red-700 ring-1 ring-red-600/20', dot: 'bg-red-500' };
      case 'expired':
        return { label: 'Expired', cls: 'bg-gray-50 text-gray-600 ring-1 ring-gray-500/20', dot: 'bg-gray-400' };
      default:
        return {
          label: status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown',
          cls: 'bg-gray-50 text-gray-600 ring-1 ring-gray-500/20',
          dot: 'bg-gray-400',
        };
    }
  };

  return (
    <>
                <div className="space-y-4">
                  {/* Header with create button */}
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-1.5 overflow-x-auto pb-1 -mb-1 scrollbar-hide">
                      {(['all', 'active', 'past_due', 'pending', 'cancelled', 'expired'] as const).map(filter => (
                        <button
                          key={filter}
                          onClick={() => setSubscriptionFilter(filter)}
                          className={`px-2.5 py-1.5 rounded-full text-[12px] sm:text-[13px] font-medium whitespace-nowrap transition-colors ${
                            subscriptionFilter === filter
                              ? 'bg-gray-900 text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {filter === 'past_due' ? 'Past due' : filter.charAt(0).toUpperCase() + filter.slice(1)}
                          {subscriptionCounts[filter] > 0 && (
                            <span className={`ml-1 ${subscriptionFilter === filter ? 'text-gray-300' : 'text-gray-400'}`}>
                              {subscriptionCounts[filter]}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={() => setIsCreateSubscriptionModalOpen(true)}
                      className="w-full sm:w-auto px-4 py-2.5 sm:py-2 bg-blue-600 text-white text-[13px] font-medium rounded-[10px] hover:bg-blue-700 transition-colors whitespace-nowrap text-center"
                    >
                      + New Subscription
                    </button>
                  </div>

                  {loadingSubscriptions ? (
                    <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] p-12 flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                    </div>
                  ) : filteredSubscriptions.length === 0 ? (
                    <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                      <div className="flex flex-col items-center justify-center py-16 px-4">
                        <div className="w-12 h-12 bg-blue-50 rounded-[12px] flex items-center justify-center mx-auto mb-4">
                          <RefreshCw className="w-6 h-6 text-blue-600" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2" style={{ letterSpacing: '-0.01em' }}>
                          {subscriptionFilter === 'all' ? 'No Subscriptions Yet' : `No ${subscriptionFilter} subscriptions`}
                        </h3>
                        <p className="text-[15px] text-gray-500 text-center max-w-md" style={{ letterSpacing: '-0.01em' }}>
                          {subscriptionFilter === 'all'
                            ? 'Create recurring subscription plans for your customers.'
                            : `There are no subscriptions with ${subscriptionFilter} status.`}
                        </p>
                        {subscriptionFilter === 'all' && (
                          <button
                            onClick={() => setIsCreateSubscriptionModalOpen(true)}
                            className="mt-4 px-4 py-2 bg-blue-600 text-white text-[13px] font-medium rounded-[10px] hover:bg-blue-700 transition-colors"
                          >
                            Create Subscription
                          </button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                      {/* Desktop table */}
                      <div className="hidden md:block">
                        <table className="w-full table-fixed divide-y divide-gray-200">
                          <thead className="bg-gray-50/80">
                            <tr>
                              <th className="w-[32%] px-4 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Subscription</th>
                              <th className="w-[22%] px-4 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Customer</th>
                              <th className="w-[12%] px-4 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Amount</th>
                              <th className="w-[16%] px-4 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Frequency</th>
                              <th className="w-[18%] px-4 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Next billing date</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {filteredSubscriptions.map((sub) => {
                              const freqLabel = sub.interval_count === 1
                                ? (sub.interval === 'day' ? 'Daily' : sub.interval === 'week' ? 'Weekly' : sub.interval === 'month' ? 'Monthly' : 'Yearly')
                                : `Every ${sub.interval_count} ${sub.interval}s`;
                              const customerFullName = sub.customers ? `${sub.customers.first_name || ''} ${sub.customers.last_name || ''}`.trim() : '';
                              const customerName = customerFullName || sub.customer_email || sub.customer_phone || 'No customer';
                              const renewal = getNextRenewal(sub);
                              return (
                                <tr
                                  key={sub.id}
                                  className="hover:bg-gray-50/60 cursor-pointer transition-colors"
                                  onClick={() => setSelectedSubscription(sub)}
                                >
                                  <td className="px-4 py-3.5">
                                    <div className="text-[14px] font-medium text-gray-900 truncate" style={{ letterSpacing: '-0.01em' }}>{sub.name}</div>
                                    <div className="flex items-center gap-1.5 mt-1">
                                      {(() => { const b = subscriptionStatusBadge(sub.status); return (
                                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold flex-shrink-0 ${b.cls}`}>
                                          <span className={`w-1.5 h-1.5 rounded-full mr-1 ${b.dot}`} />
                                          {b.label}
                                        </span>
                                      ); })()}
                                      {(() => { const b = subscriptionModeBadge(sub); return b ? (
                                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold flex-shrink-0 ${b.cls}`}>{b.label}</span>
                                      ) : null; })()}
                                    </div>
                                    <div className="text-[12px] text-gray-500 mt-0.5 truncate max-w-[280px]">
                                      {[sub.subscription_number, sub.description].filter(Boolean).join(' · ')}
                                    </div>
                                  </td>
                                  <td className="px-4 py-3.5">
                                    <div className="flex items-center gap-2 min-w-0">
                                      <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                                        <Users className="w-3.5 h-3.5 text-gray-500" />
                                      </div>
                                      <div>
                                        <div className="text-[13px] font-medium text-gray-900 truncate max-w-[160px]">{customerName}</div>
                                        {sub.customers?.email && customerFullName && (
                                          <div className="text-[11px] text-gray-400 truncate max-w-[160px]">{sub.customers.email}</div>
                                        )}
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-4 py-3.5">
                                    <div className="text-[14px] font-semibold text-gray-900 whitespace-nowrap">
                                      ${Number(sub.amount).toFixed(2)}
                                    </div>
                                    <div className="text-[11px] text-gray-400 uppercase">{sub.currency}</div>
                                  </td>
                                  <td className="px-4 py-3.5 text-[13px] text-gray-700 whitespace-nowrap">
                                    {freqLabel}
                                  </td>
                                  <td className="px-4 py-3.5">
                                    {renewal ? (
                                      <div>
                                        <div className="text-[13px] font-medium text-gray-900 whitespace-nowrap">
                                          {renewal.date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                                        </div>
                                        <div className={`text-[11px] font-medium ${
                                          renewal.daysUntil < 0 ? 'text-red-500' :
                                          renewal.daysUntil <= 3 ? 'text-red-500' :
                                          renewal.daysUntil <= 7 ? 'text-yellow-600' :
                                          'text-gray-400'
                                        }`}>
                                          {renewal.daysUntil < 0 ? 'Overdue' :
                                           renewal.daysUntil === 0 ? 'Today' :
                                           renewal.daysUntil === 1 ? 'Tomorrow' :
                                           `In ${renewal.daysUntil} days`}
                                        </div>
                                      </div>
                                    ) : (
                                      <span className="text-[12px] text-gray-400">
                                        {sub.status === 'pending' ? 'Not started' :
                                         sub.status === 'cancelled' ? 'Cancelled' : '—'}
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {/* Mobile card list */}
                      <div className="md:hidden divide-y divide-gray-100">
                        {filteredSubscriptions.map((sub) => {
                          const freqLabel = sub.interval_count === 1
                            ? (sub.interval === 'day' ? 'Daily' : sub.interval === 'week' ? 'Weekly' : sub.interval === 'month' ? 'Monthly' : 'Yearly')
                            : `Every ${sub.interval_count} ${sub.interval}s`;
                          const customerFullName = sub.customers ? `${sub.customers.first_name || ''} ${sub.customers.last_name || ''}`.trim() : '';
                          const customerName = customerFullName || sub.customer_email || sub.customer_phone || 'No customer';
                          const renewal = getNextRenewal(sub);
                          return (
                            <div
                              key={sub.id}
                              className="px-4 py-3.5 hover:bg-gray-50/60 cursor-pointer transition-colors active:bg-gray-100/60"
                              onClick={() => setSelectedSubscription(sub)}
                            >
                              {/* Row 1: Name + Status */}
                              <div className="flex items-center justify-between gap-2 mb-1.5">
                                <div className="flex items-center gap-1.5 min-w-0">
                                  <div className="text-[14px] font-medium text-gray-900 truncate" style={{ letterSpacing: '-0.01em' }}>{sub.name}</div>
                                  {(() => { const b = subscriptionStatusBadge(sub.status); return (
                                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold flex-shrink-0 ${b.cls}`}>
                                      <span className={`w-1.5 h-1.5 rounded-full mr-1 ${b.dot}`} />
                                      {b.label}
                                    </span>
                                  ); })()}
                                  {(() => { const b = subscriptionModeBadge(sub); return b ? (
                                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold flex-shrink-0 ${b.cls}`}>{b.label}</span>
                                  ) : null; })()}
                                </div>
                              </div>

                              {/* Row 2: Customer */}
                              <div className="text-[12px] text-gray-500 truncate mb-2">{customerName}</div>

                              {/* Row 3: Amount + Frequency + Renewal */}
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-1.5 text-[13px]">
                                  <span className="font-semibold text-gray-900">${Number(sub.amount).toFixed(2)}</span>
                                  <span className="text-gray-300">/</span>
                                  <span className="text-gray-500">{freqLabel.toLowerCase()}</span>
                                </div>
                                {renewal ? (
                                  <div className={`text-[11px] font-medium ${
                                    renewal.daysUntil <= 3 ? 'text-red-500' :
                                    renewal.daysUntil <= 7 ? 'text-yellow-600' :
                                    'text-blue-600'
                                  }`}>
                                    {renewal.daysUntil < 0 ? 'Billing overdue' :
                                     renewal.daysUntil === 0 ? 'Bills today' :
                                     renewal.daysUntil === 1 ? 'Bills tomorrow' :
                                     `Bills ${renewal.date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`}
                                  </div>
                                ) : (
                                  <div className="text-[11px] text-gray-400">
                                    {sub.status === 'pending' ? 'Not started' : sub.status === 'cancelled' ? 'Cancelled' : ''}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

      <CreateSubscriptionModal
        isOpen={isCreateSubscriptionModalOpen}
        onClose={() => setIsCreateSubscriptionModalOpen(false)}
        onSuccess={() => {
          setIsCreateSubscriptionModalOpen(false);
          loadSubscriptions();
        }}
      />

      {selectedSubscription && (
        <EditSubscriptionModal
          isOpen={!!selectedSubscription}
          onClose={() => setSelectedSubscription(null)}
          subscription={selectedSubscription}
          onUpdate={() => {
            setSelectedSubscription(null);
            loadSubscriptions();
          }}
        />
      )}
    </>
  );
}
